import { Card } from '@app/components/various/Card';
import { Status } from '@app/components/various/Status';
import { getOrderPathByStatus } from '@app/logic/Orders';
import { useOrderStatusLabels } from '@app/utils/hooks/useOrderStatusLabels';
import { isDefined } from '@app/utils/is';
import { getOrderStatusColor } from '@app/utils/status';
import { Row } from '@tanstack/react-table';
import { DeliveryWindow, Order } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface SplitOrder {
  deliveryWindowsIds: string[];
  key: string;
  orderId: string;
  orderNumber: string;
  status: Order.Status;
  total: string;
  prepaymentAmount: string;
  displayDate: string | null;
  units: number;
}

interface Props {
  row: Row<SplitOrder>;
  deliveryWindows: DeliveryWindow.Mixed[];
}

export const CheckoutSplitCard = ({ row, deliveryWindows }: Props) => {
  const { t } = useTranslation(['common', 'checkout']);
  const statusLabels = useOrderStatusLabels();

  const { status, orderId, orderNumber, deliveryWindowsIds, units, total, prepaymentAmount, displayDate } = row.original;
  const orderLink = isDefined(status) ? getOrderPathByStatus(orderId, status) : '';
  const filteredDeliveryWindows = React.useMemo(
    () => deliveryWindows.filter(deliveryWindow => deliveryWindowsIds?.includes(deliveryWindow.deliveryWindow)),
    [deliveryWindows, deliveryWindowsIds],
  );

  return (
    <Card.Wrapper markerColor={getOrderStatusColor(status)}>
      <Card.Header>
        {isDefined(status) && (
          <>
            <Status type={status} />
            <span>
              {t('common:status')}: <span className="text-neutral-20">{statusLabels[status]}</span>
            </span>
          </>
        )}
      </Card.Header>
      <Card.Title>
        <Link to={orderLink} className="rounded-small absolute-cursor-pointer focus-visible:focus-outline">
          #{orderNumber}
        </Link>
      </Card.Title>
      <Card.AttributeList>
        <Card.Attribute name={t('common:delivery_window_other')} value={filteredDeliveryWindows.map(({ name }) => name).join(', ')} />
        <Card.Attribute name={t('checkout:prepayment_split_by_order')} value={prepaymentAmount} />
        <Card.Attribute name={t('checkout:order_date')} value={displayDate} />
      </Card.AttributeList>
      <Card.SummaryList>
        <Card.Summary name={t('common:total_for_units', { count: units })} value={total} />
      </Card.SummaryList>
    </Card.Wrapper>
  );
};
